import { Get, Post, Put, Delete } from 'config/apiServiceConfig'

// ============ Response types ============

// --- PWP List (getPwpData) ---
export interface PWPListParamsType {
  search?: string
  status?: string
  startDate?: number
  endDate?: number
  sort?: string
  direction?: string
  pageSize?: number
  pageIndex?: number
}

export interface PWPProductListParamsType {
  search?: string
  selectedIds?: string
  pageSize?: number
  pageIndex?: number
}

export interface PWPFormProductQuotaType {
  location_hub_id: number
  quota_campaign: number
  quota_per_day: number
  status: string
}

export interface PWPFormProductType {
  product_id: number
  product_name: string
  sku: string
  discount: number
  limit: number
  pwp_product_quota: PWPFormProductQuotaType[]
}

export interface PWPCreateRuleType {
  key: string
  value_int: number[]
  is_exclude: boolean
}

export interface PWPCreatePayloadType {
  campaign_name: string
  start_date: string | number
  start_time: string | number
  end_date: string | number
  end_time: string | number
  is_all_day: boolean
  limit_campaign: number
  limit_per_day: number
  minimum_spend: number
  description: string
  status: string
  is_targeted: boolean
  is_new_user: boolean
  targeting_type: string
  segment_id: number
  pwp_products: PWPFormProductType[]
  rules: PWPCreateRuleType[] | null
  skp: { skp_id: number; skp_name: string }
}

export interface PWPErrorResponseType {
  error?: { status?: boolean; message?: string }
}

export interface PWPUpdateStatusPayloadType {
  status: string
}

export interface PWPTargetedUserParamsType {
  pageIndex?: number
  pageSize?: number
}

export interface PWPPrioritySearchParamsType {
  search?: string
}

export interface PWPSkpParamsType {
  skp_no?: string
  promotion_type?: string
  campaign_name?: string
  status?: string
}

// --- PWP List (getPwpData) ---

export interface PWPListItemType {
  id: number
  campaignName: string
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  campaignType: string
  isTargeted: boolean
  uploadStatus: { name: string; value: string } | null
  countMoreHub: number
  status: string
  locationHubDetails: { locationName: string }[]
}

export interface PWPListResponseType {
  data: {
    list: PWPListItemType[]
  }
  pagination: {
    pageSize: number
    pageIndex: number
    totalPages: number
    totalElements: number
    numberOfElements: number
  }
}

// --- PWP Detail (getPwpDetail) ---

export interface PWPDProductQuotaType {
  location_hub_id: number
  location_name: string
  quota_campaign: number
  quota_per_day: number
  stock: number
  status: string
  price: number
}

export interface PWPDProductType {
  product_id: number
  product_name: string
  sku: string
  discount: number
  limit: number
  pwp_product_quota: PWPDProductQuotaType[]
}

export interface PWPRuleDetailType {
  key: string
  items: { id: number; name: string }[]
  is_exclude: boolean
  isChecked: boolean
}

export interface PWPDetailType {
  campaign_name: string
  start_date: string
  start_time: string
  end_date: string
  end_time: string
  is_all_day: boolean
  limit_campaign: number
  limit_per_day: number
  minimum_spend: number
  description: string
  status: string
  targeting_type: string
  segment_id: number
  segment_name: string
  is_new_user: boolean
  is_targeted: boolean
  pwp_products: PWPDProductType[]
  rules: PWPRuleDetailType[]
  skp: {
    skp_id: number
    skp_name: string
  }
}

export interface PWPDetailResponseType {
  data: {
    pwp_detail: PWPDetailType
  }
}

// --- PWP Type (getPwpType) ---

export interface PWPTypeItemType {
  value: string
  label: string
}

// --- PWP Product List (getPwpProduct) ---

export interface PWPListProductHubType {
  location_hub_id: number
  location_hub_name: string
  product_price: number
  is_active: boolean
  stock: number
}

export interface PWPListProductType {
  product_id: number
  product_name: string
  product_sku_number: string
  product_images: { image_url: string }[]
  gwp_detail_location_hub: PWPListProductHubType[]
}

export interface PWPProductListResponseType {
  data: {
    numberOfElements: number
    totalElements: number
    totalPages: number
    size: number
    number: number
    content: PWPListProductType[]
  }
}

// --- PWP Bulk Upload (postPwpBulkUpload) ---

export interface PWPBulkUploadContentType extends PWPListProductType {
  details: {
    location_hub_id: number
    location_name: string
    product_price: number
    stock: number
    is_active: boolean
  }[]
}

export interface PWPBulkUploadResponseType {
  data: {
    content: PWPBulkUploadContentType[]
  }
}

// --- SKP (getPwpCommercialInternalSkp) ---

export interface PWPSkpItemType {
  id: number
  skp_no: string
}

// --- Targeted User List (getTargetedUserList) ---

export interface PWPTargetedUserItemType {
  id: number
  name: string
}

export interface PWPTargetedUserResponseType {
  data: { list: PWPTargetedUserItemType[] }
  pagination: { totalElements: number }
}

// --- PWP Priority (getPWPPriority) ---

export interface PWPPriorityItemType {
  title: string
  order: number
  id: number
  skuNumber: string
  pwpProductId: number
}

// ============ API functions ============

export const getPwpData = (params: PWPListParamsType) =>
  Get<PWPListResponseType>({ url: '/api/promo/pwp', params })

export const getPwpDetail = (id: string | number) =>
  Get<PWPDetailResponseType>({
    config: {
      baseURL: process.env.REACT_APP_BASE_URL_KONG_API,
    },
    url: `/promo/internal/v1/pwp/${id}`,
  })

export const getPwpType = () =>
  Get<{ data: PWPTypeItemType[] }>({ url: '/api/promo/pwp/rules-type' })

export const getPwpProduct = (params: PWPProductListParamsType) =>
  Get<PWPProductListResponseType>({
    url: '/api/gwp/product',
    params: { excludeVB: true, ...params },
  })

export const postPwpBulkUpload = (payload: FormData) =>
  Post<PWPBulkUploadResponseType>({
    url: '/api/promo/pwp/bulk-add-product',
    data: payload,
    contentType: 'multipart/form-data',
  })

export const postPwpCreate = (payload: PWPCreatePayloadType) =>
  Post<PWPErrorResponseType>({
    config: {
      baseURL: process.env.REACT_APP_BASE_URL_KONG_API,
    },
    url: '/promo/internal/v1/pwp',
    data: payload,
  })

export const putPwpEdit = (id: string | number, payload: PWPCreatePayloadType) =>
  Put<PWPErrorResponseType>({
    config: {
      baseURL: process.env.REACT_APP_BASE_URL_KONG_API,
    },
    url: `/promo/internal/v1/pwp/${id}`,
    data: payload,
  })

export const putPwpEditStatus = (id: string | number, payload: PWPUpdateStatusPayloadType) =>
  Put({ url: `/api/promo/pwp/${id}/status`, data: payload })

export const getTargetedUserList = ({
  id,
  params,
}: {
  id: number
  params: PWPTargetedUserParamsType
}) => Get<PWPTargetedUserResponseType>({ url: `/api/promo/pwp/${id}/targeted-user-list`, params })

export const deleteTargetedUser = ({
  customerID,
  campaignID,
}: {
  customerID: number
  campaignID: number
}) => Delete({ url: `/api/promo/pwp/${campaignID}/${customerID}` })

export interface PWPPrioritySearchProductType {
  fullName: string
  pwpProductId: number
  skuNumber: string
}

export const getSearchPWPProductPriority = ({ params }: { params: PWPPrioritySearchParamsType }) =>
  Get<{ data: PWPPrioritySearchProductType[] }>({
    url: '/api/promo/pwp/products',
    params: { limit: 20, page: 0, ...params },
  })

export const getPWPPriority = () =>
  Get<{ data: PWPPriorityItemType[] }>({ url: '/api/promo/pwp/pinned-products' })

export const postPWPPriority = ({
  payload,
}: {
  payload: { data: { id: number; sortOrder: number }[] }
}) => Post({ url: '/api/promo/pwp/pinned-products', data: payload })

export const getPwpCommercialInternalSkp = (params: PWPSkpParamsType) =>
  Get<{ data: PWPSkpItemType[] }>({
    config: {
      baseURL: process.env.REACT_APP_BASE_URL_KONG_API,
    },
    url: '/commercial/internal/v1/skp',
    params,
  })
